import React, { useMemo } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { normalizeDisplayPowerUnit } from '../utils/displayPowerUnit';
import SolarInverterGridFlow from './SolarInverterGridFlow';
import './SolarInverterGridFlow.css';

const safeToFixed = (v, decimals = 2) =>
  v != null && Number.isFinite(Number(v)) ? Number(v).toFixed(decimals) : '0.00';

/**
 * Rrjedhje Solar → Inverter → GRID për matës energjie të kompanive që NUK janë HIB.
 * Mobile: `compactMobile` (default true). Desktop: `compactMobile={false}` + `desktopEmbed` sidebar | hero.
 */
export default function EnergyMeterSolidSolarFlow({
  displayPowerUnit,
  powerFlowKw = {},
  activePowerMW,
  useKw = false,
  isDark = false,
  className = '',
  compactMobile = true,
  /** `hero` = band e gjerë (Chint web); `sidebar` = kolonë e ngushtë */
  desktopEmbed = 'sidebar',
}) {
  const { t } = useLanguage();
  const unit = normalizeDisplayPowerUnit(displayPowerUnit);
  const { ppvKw = 0, gridImportKw = 0 } = powerFlowKw;

  const flowActive = useMemo(() => {
    const p = Number(ppvKw);
    const g = Number(gridImportKw);
    return (Number.isFinite(p) && p > 0.02) || (Number.isFinite(g) && Math.abs(g) > 0.02);
  }, [ppvKw, gridImportKw]);

  const { gridMw, gridUnit } = useMemo(() => {
    const apRaw =
      activePowerMW != null && Number.isFinite(Number(activePowerMW))
        ? Math.abs(Number(activePowerMW))
        : null;
    if (apRaw != null) {
      if (useKw) {
        return { gridMw: safeToFixed(apRaw * 1000, 2), gridUnit: t('emPowerFlowUnitKw') };
      }
      return { gridMw: safeToFixed(apRaw, 2), gridUnit: t('emPowerFlowUnitMw') };
    }
    const g = Math.abs(Number(gridImportKw) || 0);
    if (unit === 'kw') {
      return { gridMw: safeToFixed(g, 2), gridUnit: t('emPowerFlowUnitKw') };
    }
    const mw = g / 1000;
    return { gridMw: safeToFixed(mw, 3), gridUnit: t('emPowerFlowUnitMw') };
  }, [activePowerMW, gridImportKw, unit, useKw, t]);

  const surface = isDark ? 'sigf-meter-solid--dark' : 'sigf-meter-solid--light';

  const meterSizeClass = compactMobile
    ? 'sigf-root--meter-mobile-compact'
    : desktopEmbed === 'hero'
      ? 'sigf-root--meter-desktop-hero'
      : 'sigf-root--meter-desktop-sidebar';

  return (
    <SolarInverterGridFlow
      className={[
        'sigf-root--meter-embed',
        'sigf-root--meter-solid',
        meterSizeClass,
        surface,
        'sigf-root--dashboard',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      brandLabel=""
      showSummaryFooter={false}
      showExportTag
      totalMw=""
      inverterCount={0}
      gridMw={gridMw}
      gridUnit={gridUnit}
      flowActive={flowActive}
      exportTagLabel={t('emPowerFlowExportTag')}
      inverterLabel={t('inverter')}
    />
  );
}
